import type { TopicFatiguePoint } from "@nexus/contracts";
import { humanizeBrandName } from "./humanizeBrandName.js";

/** Same tokens as TopConceptsOvertimeChart — chart-5 (error red) is skipped. */
export const FATIGUE_SERIES_COLORS = [
  "var(--chart-1)",
  "var(--chart-2)",
  "var(--chart-3)",
  "var(--chart-4)",
  "var(--chart-6)",
] as const;

const AGGREGATE_VALUE_KEY = "value";
const SERIES_DATA_KEY_PREFIX = "s:";

/** Age / range tokens such as `25-34`. Slugs that merely contain a digit still humanize. */
const NUMERIC_RANGE_KEY = /^\d+(-\d+)?$/;

export interface FatigueChartRow {
  date: string;
  [seriesKey: string]: string | number;
}

export interface FatigueChartSeries {
  dataKey: string;
  raw: string;
}

export interface FatigueChartModel {
  rows: FatigueChartRow[];
  series: FatigueChartSeries[];
  isSplit: boolean;
}

export function toFatigueSeriesDataKey(raw: string): string {
  return `${SERIES_DATA_KEY_PREFIX}${raw}`;
}

/**
 * Pivot long-format fatigue points into Recharts rows.
 * Missing period/series combinations are omitted (no invented zeros).
 */
export function buildFatigueChartRows(points: TopicFatiguePoint[]): FatigueChartModel {
  const isSplit = points.some((point) => point.series !== undefined);
  if (!isSplit) {
    return {
      rows: points.map((point) => ({ date: point.date, [AGGREGATE_VALUE_KEY]: point.value })),
      series: [{ dataKey: AGGREGATE_VALUE_KEY, raw: AGGREGATE_VALUE_KEY }],
      isSplit: false,
    };
  }

  const dates: string[] = [];
  const seenDates = new Set<string>();
  const series: FatigueChartSeries[] = [];
  const seenRaw = new Set<string>();

  for (const point of points) {
    if (!seenDates.has(point.date)) {
      seenDates.add(point.date);
      dates.push(point.date);
    }
    const raw = point.series ?? AGGREGATE_VALUE_KEY;
    if (!seenRaw.has(raw)) {
      seenRaw.add(raw);
      series.push({ dataKey: toFatigueSeriesDataKey(raw), raw });
    }
  }

  const byDate = new Map<string, FatigueChartRow>();
  for (const date of dates) {
    byDate.set(date, { date });
  }
  for (const point of points) {
    const row = byDate.get(point.date);
    if (!row) continue;
    const raw = point.series ?? AGGREGATE_VALUE_KEY;
    row[toFatigueSeriesDataKey(raw)] = point.value;
  }

  return {
    rows: dates.map((date) => byDate.get(date)!),
    series,
    isSplit: true,
  };
}

export function fatigueSeriesColor(index: number): string {
  return FATIGUE_SERIES_COLORS[index % FATIGUE_SERIES_COLORS.length]!;
}

/** Humanize slugs (audience_network → Audience Network); leave numeric keys like 25-34 intact. */
export function fatigueSeriesDisplayName(raw: string): string {
  if (NUMERIC_RANGE_KEY.test(raw)) return raw;
  return humanizeBrandName(raw);
}
